import AppStoreContext from 'contexts/AppStoreContext';
import ECSEditorContext from 'contexts/ECSEditorContext';
import ECSOverlayContext from 'contexts/ECSOverlayContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import MarkupKitContext from 'contexts/MarkupKitContext';
import CarnivoreSheet from 'editor/components/CarnivoreSheet';
import FocusedViewport from 'editor/components/FocusedViewport';
import { Components } from 'editor/components';
import Markup from 'editor/components/Markup';
import TileSheet from 'editor/components/TileSheet';
import noop from 'lodash/noop';
import { useProjectManager } from 'providers/ProjectManagerProvider';
import React, { FC, useContext, useEffect, useState } from 'react';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { PostRFI } from 'records/interactions/PostRFI';
import { Markup as MarkupRecord } from 'records/Markup';
import { Entity } from '$legacyecstatic';
import MarkupAnchorPortal from './MarkupAnchors/MarkupAnchorPortal';
import RemoveMarkupButton from './MarkupAnchors/RemoveMarkupButton';

type Props = {
  markup: MarkupRecord;
  sheetComp: CarnivoreSheet;
};

const ECSSheetMarkup: FC<Props> = (props: Props) => {
  const { markup, sheetComp } = props;
  const world = useContext(ECSWorldContext);
  const appStore = useContext(AppStoreContext);
  const markupKit = useContext(MarkupKitContext);
  const overlay = useContext(ECSOverlayContext);
  const { doc } = useContext(ECSEditorContext);
  const [markupComp, setMarkupComp] = useState<Markup | undefined>(undefined);
  const { actions, state: appState } = appStore;
  const projectManager = useProjectManager();
  const project = projectManager.getSelectedProject();
  const canDeleteRFIPermission = project.permissions.createRfi;

  useEffect(() => {
    const { rfis, punches } = appStore.state;

    const _markupComp = new Markup(markup);
    _markupComp.selected = false;

    const rfi = rfis.find((r) => r.postedmarkupids.contains(markup.markupid));

    if (rfi) {
      if (rfi.answer === null && rfi.previousversionid === '') {
        return noop;
      }

      _markupComp.isRFI = true;
    }

    if (punches.find((p) => p.markupids.contains(markup.markupid))) {
      _markupComp.isPunch = true;
      actions.setLocalMarkup(_markupComp.markup);
    }

    const createPunchInteraction = appStore.state.interactions.find(
      (interaction) => interaction.type === 'CreatePunch'
    );

    if (createPunchInteraction) {
      const createdPunch = createPunchInteraction as CreatePunch;
      const interactionHasMarkup = createdPunch.punch.markups.some(
        (m) => m.clientGeneratedID === markup.markupid
      );
      if (interactionHasMarkup) {
        _markupComp.selected = true;
        _markupComp.isPunch = true;
        _markupComp.isNewPunch = true;
      }
    }
    const _markupEntity = world
      .createEntity()
      .add(_markupComp)
      .add(markupKit)
      .add(overlay);

    setMarkupComp(_markupComp);

    return () => {
      // Ensure entity is still in world before trying to destroy it.
      if (world.entities.get(_markupEntity.id)) {
        _markupEntity.destroyImmediately();
      }
    };
  }, []);

  useEffect(() => {
    if (markupComp && appState.focusedMarkup === markupComp.markup.markupid) {
      markupComp.selected = true;

      world
        .createEntity()
        .add(doc)
        .add(markupKit)
        .add(overlay)
        .add(markupComp)
        .add(new FocusedViewport(sheetComp.sheet.height));

      actions.clearFocusedMarkup();
    }
  }, [markupComp, appState]);

  useEffect(() => {
    if (markupComp && markupComp.markup !== markup) {
      markupComp.markup = markup;
      markupComp.mkMarkup = null;
    }
  }, [markupComp, markup]);

  async function handleRemoveClick() {
    if (markupComp) {
      const postRFIInteraction = appStore.state.interactions.find(
        (ii) => ii.type === 'PostRFI'
      );

      if (postRFIInteraction) {
        const interactionWithoutMarkup = (postRFIInteraction as PostRFI)
          .delete('markup')
          .set('updatingMarkup', false)
          .set('removingMarkup', true);
        appStore.actions.updateInteraction(interactionWithoutMarkup);

        await appStore.actions.deleteMarkup(markupComp.markup);
      } else {
        actions.deleteMarkup(markupComp.markup);
      }
    }
  }

  const hasPostRFIInteraction =
    appStore.state.interactions.filter((i) => i.type === 'PostRFI').size > 0;

  return markupComp?.selected &&
    !markupComp?.isPunch &&
    (!markupComp.isRFI || hasPostRFIInteraction) &&
    canDeleteRFIPermission ? (
    <MarkupAnchorPortal markupComp={markupComp} sheetComp={sheetComp}>
      <RemoveMarkupButton
        id={`remove-markup-${markupComp.markup.markupid}`}
        onRemoveClick={handleRemoveClick}
      />
    </MarkupAnchorPortal>
  ) : null;
};

export default ECSSheetMarkup;
